/**
 * Cards Layout Options
 * 
 * Options panel for configuring the cards layout.
 * Note: Sort and size controls are now in the CardsLayout header.
 */

import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { LayoutOptionsProps, CardsLayoutOptions } from '../types';

// Special value for "none" selection (Select doesn't allow empty string)
const NONE_VALUE = '__none__';

// Extract field name from template like "{{ field_name }}"
function extractFieldFromTemplate(template?: string): string | undefined {
    if (!template) return undefined;
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    return match?.[1];
}

export function CardsOptions({
    layoutOptions,
    onLayoutOptionsChange,
    collection: _collection,
    fields,
}: LayoutOptionsProps<CardsLayoutOptions>) {

    // Get fields that could be used for images (file relations)
    const imageFields = useMemo(() => {
        return fields.filter(f => {
            // Look for file-related fields
            if (f.field === '$thumbnail') return true;
            // Check for file type or UUID fields that might be file references
            const type = f.type?.toLowerCase() || f.schema?.data_type?.toLowerCase();
            return type === 'uuid' || f.meta?.interface === 'file-image' || f.meta?.interface === 'file';
        });
    }, [fields]);

    // Get text fields for title/subtitle templates
    const textFields = useMemo(() => {
        return fields.filter(f => {
            const type = f.type?.toLowerCase() || f.schema?.data_type?.toLowerCase();
            return ['string', 'text', 'varchar', 'char'].includes(type || '');
        });
    }, [fields]);

    // Extract current values
    const currentImageSource = useMemo(() => layoutOptions.imageSource || NONE_VALUE, [layoutOptions.imageSource]);
    const currentTitle = useMemo(() => extractFieldFromTemplate(layoutOptions.title) || NONE_VALUE, [layoutOptions.title]);
    const currentSubtitle = useMemo(() => extractFieldFromTemplate(layoutOptions.subtitle) || NONE_VALUE, [layoutOptions.subtitle]);
    const currentImageFit = useMemo(() => layoutOptions.imageFit || 'crop', [layoutOptions.imageFit]);

    const handleChange = (key: keyof CardsLayoutOptions, value: unknown) => {
        onLayoutOptionsChange({ ...layoutOptions, [key]: value });
    };

    return (
        <div className="space-y-4">
            {/* Image Source */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Image Source</Label>
                <Select
                    key={`image-${currentImageSource}`}
                    value={currentImageSource}
                    onValueChange={(v) => handleChange('imageSource', v === NONE_VALUE ? undefined : v)}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select image field" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NONE_VALUE}>None</SelectItem>
                        {imageFields.map(f => (
                            <SelectItem key={f.field} value={f.field}>
                                {f.meta?.note || f.field}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Title Template */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Title</Label>
                <Select
                    key={`title-${currentTitle}`}
                    value={currentTitle}
                    onValueChange={(v) => handleChange('title', v === NONE_VALUE ? undefined : `{{ ${v} }}`)}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select title field" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NONE_VALUE}>None</SelectItem>
                        {textFields.map(f => (
                            <SelectItem key={f.field} value={f.field}>
                                {f.meta?.note || f.field}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Subtitle Template */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Subtitle</Label>
                <Select
                    key={`subtitle-${currentSubtitle}`}
                    value={currentSubtitle}
                    onValueChange={(v) => handleChange('subtitle', v === NONE_VALUE ? undefined : `{{ ${v} }}`)}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select subtitle field" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NONE_VALUE}>None</SelectItem>
                        {textFields.map(f => (
                            <SelectItem key={f.field} value={f.field}>
                                {f.meta?.note || f.field}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Image Fit */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Image Fit</Label>
                <Select
                    key={`fit-${currentImageFit}`}
                    value={currentImageFit}
                    onValueChange={(v) => handleChange('imageFit', v as 'crop' | 'contain')}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select fit mode" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="crop">Crop</SelectItem>
                        <SelectItem value="contain">Contain</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
